import * as React from "react";
import { Wallet, Receipt, Users, CalendarPlus, CheckCircle, XCircle } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { CustomerDetailLabels, CustomerProfileStats } from "./types";
import { formatMoney } from "./utils";

interface Props {
  stats: CustomerProfileStats;
  labels: CustomerDetailLabels;
}

export function CustomerStatStrip({ stats, labels }: Props) {
  const items: Array<{
    label: string;
    value: string;
    icon: React.ReactNode;
    tone?: "primary" | "accent" | "success" | "warning" | "danger" | "info";
  }> = [
    {
      label: labels.stats.totalSpend,
      value: formatMoney(stats.totalSpend, stats.currency),
      icon: <Wallet size={16} weight="regular" />,
      tone: "primary",
    },
    {
      label: labels.stats.averageTicket,
      value: formatMoney(stats.averageTicket, stats.currency),
      icon: <Receipt size={16} weight="regular" />,
      tone: "accent",
    },
    {
      label: labels.stats.visits,
      value: String(stats.visitCount),
      icon: <Users size={16} weight="regular" />,
    },
    {
      label: labels.stats.upcoming,
      value: String(stats.upcomingCount),
      icon: <CalendarPlus size={16} weight="regular" />,
      tone: "info",
    },
    {
      label: labels.stats.completed,
      value: String(stats.completedCount),
      icon: <CheckCircle size={16} weight="regular" />,
      tone: "success",
    },
    {
      label: labels.stats.cancelled,
      value: String(stats.cancelledCount),
      icon: <XCircle size={16} weight="regular" />,
      tone: "danger",
    },
  ];

  const toneToClass: Record<string, string> = {
    primary: "text-biz-primary bg-biz-primary-subtle",
    accent: "text-biz-accent bg-biz-accent-subtle",
    success: "text-biz-success bg-biz-success-subtle",
    warning: "text-biz-warning bg-biz-warning-subtle",
    danger: "text-biz-danger bg-biz-danger-subtle",
    info: "text-biz-info bg-biz-info-subtle",
  };

  return (
    <div
      className="grid gap-3 px-6 py-4 border-b border-biz-border bg-biz-bg
        grid-cols-2 sm:grid-cols-3 lg:grid-cols-6"
      role="list"
      aria-label={labels.stats.totalSpend}
    >
      {items.map((it) => (
        <div
          key={it.label}
          role="listitem"
          className="flex items-center gap-3 rounded-biz-card border border-biz-border bg-biz-surface px-4 py-3 shadow-biz-sm"
        >
          <div
            className={cn(
              "h-9 w-9 shrink-0 rounded-biz-btn flex items-center justify-center",
              it.tone ? toneToClass[it.tone] : "bg-biz-muted text-biz-foreground-muted",
            )}
            aria-hidden
          >
            {it.icon}
          </div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-biz-foreground-subtle font-semibold truncate">
              {it.label}
            </div>
            <div className="text-lg font-display font-bold text-biz-foreground font-mono-num leading-tight truncate">
              {it.value}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
